import type { ConsultDynamicWaveRuleRequest } from '@/api/rule-center/dynamic-rule';
import { VuexModule, Module, Mutation } from 'vuex-module-decorators';

export interface AiChatMessage {
  role: 'user' | 'ai';
  id: string;
  content: string;
  session_id?: string;
  task_id?: number;
  dialogue_status?: number;
  feedback_status?: number;
}

export interface AiChatConsultResult {
  answer: string;
  session_id: string;
  task_id?: number;
  dialogue_status?: number;
  feedback_status?: number;
  follow_up_questions?: string[];
}

export type AiChatConsultFn = (
  userPrompt: string,
  sessionId: string,
  signal?: AbortSignal
) => Promise<AiChatConsultResult>;

export type AiChatConsultExtraParamsGetter = (
  userPrompt: string,
  sessionId: string
) => Omit<ConsultDynamicWaveRuleRequest, 'user_prompt' | 'session_id'>;

export interface AiChatContext {
  key?: string;
  messages?: AiChatMessage[];
  sessionId?: string;
  loading?: boolean;
  title?: string;
  contextLabel?: string;
  contextAnswer?: string;
  consultFn?: AiChatConsultFn;
  consultExtraParamsGetter?: AiChatConsultExtraParamsGetter;
  userName?: string;
  suggestions?: string[];
}

@Module
class AiChat extends VuexModule {
  visible = false;
  chatContext: AiChatContext | null = null;
  registeredConsultFn: AiChatConsultFn | null = null;
  registeredConsultExtraParamsGetter: AiChatConsultExtraParamsGetter | null =
    null;

  @Mutation
  openAiChat(context?: AiChatContext): void {
    this.chatContext = context ?? {
      messages: [],
      sessionId: '',
      loading: false,
    };
    this.visible = true;
  }

  @Mutation
  closeAiChat(): void {
    this.visible = false;
  }

  @Mutation
  toggleAiChat(): void {
    this.visible = !this.visible;
  }

  @Mutation
  setAiChatContext(context: AiChatContext | null): void {
    this.chatContext = context;
  }

  @Mutation
  registerAiChatConsultFn(fn: AiChatConsultFn | null): void {
    this.registeredConsultFn = fn;
  }

  @Mutation
  registerAiChatConsultExtraParamsGetter(
    getter: AiChatConsultExtraParamsGetter | null
  ): void {
    this.registeredConsultExtraParamsGetter = getter;
  }
}

export default AiChat;
